import { useRouteLoaderData, useSearchParams } from '@remix-run/react';
import { type ReactNode, useEffect, useMemo, useRef, useState } from 'react';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';

import DataLayer from '~/services/DataLayer';

import ActionForm from '../ActionForm';
import RedirectToHiddenInput from '../Forms/RedirectToHiddenInput';
import StatusInputs from '../Forms/StatusInputs';

import useSignupRedirects from '~/hooks/useUtils/useSignupRedirects';
import type { RootLoaderData } from '~/root';
import { useComponentFetcher } from '../ComponentErrorBoundary/useComponentFetcher';
import useGlobalModals from '../GlobalModals/hooks/useGlobalModals';

type RenderProps = {
  isLoading: boolean;
  canLogInWithPassword: boolean;
  isAutoSubmitting: boolean;
  setIsAutoSubmitting: (value: boolean) => void;
};

type Props = {
  children: (props: RenderProps) => ReactNode;
  onSuccess?: () => void;
};

const LoginAction = ({ children, onSuccess }: Props) => {
  const fetcher = useComponentFetcher();
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const formRef = useRef<HTMLFormElement>(null);
  const { site } = useRouteLoaderData('root') as RootLoaderData;
  const { publication } = site;
  const { nextPage, nextStep } = useSignupRedirects();
  const { currentModal } = useGlobalModals();
  const [isAutoSubmitting, setIsAutoSubmitting] = useState(false);

  const canLogInWithPassword = !!publication?.has_subscription_password;

  const handleSuccess = (data: any) => {
    if (data?.dataLayerEvent) {
      DataLayer.pushEvent(data.dataLayerEvent);
    }

    if (data?.toast?.message) {
      toast.success(data.toast.message);
    }

    formRef.current?.reset();
    setIsAutoSubmitting(false);

    if (searchParams.get('modal') === 'login') {
      searchParams.delete('modal');
      setSearchParams(searchParams);
    }

    onSuccess?.();

    if (nextStep) {
      nextPage();
    }
  };

  const isLoading = fetcher.state === 'submitting';

  // since we need to pass translations to the dream component login form
  // we are storing the labels on the form dataset to be read by the component
  // this is a workaround since we can't pass props to the dream component
  useEffect(() => {
    if (formRef.current) {
      const dataset = formRef.current.dataset;
      const labels = {
        login_with_password_label: t('login.login_with_password_label'),
        set_new_password: t('login.set_new_password'),
        login_with_email_label: t('login.login_with_email_label'),
        login_with_otp_label: t('login.login_with_otp_label'),
        resend_otp_button_label: t('login.resend_otp_button_label'),
        otp_label: t('login.otp_label'),
      };
      Object.entries(labels).forEach(([key, value]) => {
        dataset[key] = value;
      });
    }
  }, [t]);

  // redirect_to without modal param so the modal closes
  // since the server redirects the user instead
  // of sending back a JSON
  const redirectTo = useMemo(() => {
    if (typeof document === 'undefined') {
      return;
    }

    const currentSearchParams = new URLSearchParams(document.location.search);
    const postSlug = currentSearchParams.get('postSlug');

    currentSearchParams.delete('modal');
    currentSearchParams.delete('postSlug');
    const stringifiedParams = currentSearchParams.toString();
    let baseUrl = document.location.origin;

    if (postSlug) {
      baseUrl = `${baseUrl}${postSlug}`;
    } else {
      baseUrl = `${baseUrl}${document.location.pathname}`;
    }

    if (stringifiedParams) {
      baseUrl = `${baseUrl}?${stringifiedParams}`;
    }

    return baseUrl;
  }, []);

  const otpPath = useMemo(() => {
    if (typeof document === 'undefined') {
      return;
    }

    const currentSearchParams = new URLSearchParams(document.location.search);
    currentSearchParams.set('otp', 'true');

    if (currentModal === 'login') {
      currentSearchParams.set('modal', 'login');
    }

    return `${document.location.pathname}?${currentSearchParams.toString()}`;
  }, [currentModal]);

  return (
    <ActionForm
      fetcher={fetcher}
      formRef={formRef}
      method="post"
      action="/create_login"
      onSubmit={(e) => {
        const formData = new FormData(e.currentTarget);
        if (formData.get('request_magic_link') === 'true') {
          formData.set('login_success_message', t('toast.login_link.message'));
        }

        fetcher.submit(formData, {
          method: 'post',
          action: '/create_login',
        });
      }}
      onSuccess={(data) => {
        handleSuccess(data);
      }}
      onError={(data) => {
        setIsAutoSubmitting(false);
        toast.error(data?.toast?.message || 'Something went wrong, please try again');
      }}
    >
      <RedirectToHiddenInput value={redirectTo} />
      {publication?.one_time_password_enabled && <input type="hidden" name="otp_path" value={otpPath} />}
      <input
        type="hidden"
        name="use_one_time_password"
        value={publication?.one_time_password_enabled ? 'true' : 'false'}
      />
      <input
        type="hidden"
        name="has_subscription_password"
        value={publication?.has_subscription_password ? 'true' : 'false'}
      />
      <StatusInputs
        name="login"
        errorMessage={`${t('errors.default')} ${t('errors.try_later')}`}
        successMessage={t('login.success.message')}
      />
      {children({ isLoading, canLogInWithPassword, isAutoSubmitting, setIsAutoSubmitting })}
    </ActionForm>
  );
};

export default LoginAction;
